import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Eye, RefreshCw, AlertTriangle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { reviewNotification, unreviewNotification, resendNotification, resetErrorNotification } from "@/services/notification-center-service";

// Códigos Twilio que NÃO se resolvem com retry — precisam de ação humana (corrigir cadastro).
// Espelha src/infra/handlers/twilio_error_codes.py do backend.
const TWILIO_TERMINAL_ERROR_MESSAGES: Record<string, string> = {
  "63024": "Número WhatsApp inválido — peça pro paciente confirmar o número e atualize no cadastro antes de reenviar.",
  "63049": "Mensagem bloqueada pela Meta (limite por usuário para mensagens MARKETING). Aguarde 24h ou peça opt-in antes de reenviar.",
};

export function NotificationsTable({ notifications, isLoading, onChanged }: any) {
  const [selectedNotification, setSelectedNotification] = useState<any | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [isResending, setIsResending] = useState(false);
  const [isResettingError, setIsResettingError] = useState(false);
  const { toast } = useToast();
  const { can } = useAuth();
  const canResend = can('notifications', 'resend');

  if (isLoading) return <div className="p-12 text-center text-muted-foreground">Carregando...</div>;
  if (notifications.length === 0) return <div className="p-12 text-center text-muted-foreground">Nenhuma notificação encontrada.</div>;

  const canReview = can('notifications', 'review');
  const canUnreview = can('notifications', 'unreview');

  const handleToggle = async (notification: any, nextChecked: boolean) => {
    setBusyId(notification.id);
    try {
      if (nextChecked) {
        await reviewNotification(notification.id);
        toast({ title: "Notificação revisada — será enviada na próxima rodada." });
      } else {
        await unreviewNotification(notification.id);
        toast({ title: "Notificação retornou para revisão — não será enviada." });
      }
      onChanged?.();
    } catch (error) {
      toast({
        title: nextChecked ? "Erro ao marcar como revisada" : "Erro ao reverter",
        description: error instanceof Error ? error.message : "Tente novamente.",
        variant: "destructive",
      });
    } finally {
      setBusyId(null);
    }
  };

  return (
    <>
      <div className="rounded-lg border border-border overflow-hidden bg-card">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/50 hover:bg-muted/50">
              <TableHead className="th w-[80px] text-center">Revisada</TableHead>
              <TableHead className="th w-[150px]">Categoria</TableHead>
              <TableHead className="th w-[140px]">Destinatário</TableHead>
              <TableHead className="th">Descrição</TableHead>
              <TableHead className="th w-[110px]">Status</TableHead>
              <TableHead className="th w-[130px]">Criado em</TableHead>
              <TableHead className="th w-[130px]">Enviado em</TableHead>
              <TableHead className="th w-[50px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {notifications.map((notification: any) => {
              const isReview = notification.process_status === 'REVIEW';
              const isPending = notification.process_status === 'PENDING';
              // Checkbox marcado = saiu do REVIEW (qualquer outro estado)
              const isChecked = !isReview;
              // Quando marcar (REVIEW → PENDING) requer 'review'
              // Quando desmarcar (PENDING → REVIEW) requer 'unreview' e só pode se ainda está PENDING
              const canFlip = isReview ? canReview : (isPending && canUnreview);
              return (
                <TableRow key={notification.id} className="hover:bg-muted/30">
                  <TableCell className="td text-center">
                    <Checkbox
                      checked={isChecked}
                      disabled={!canFlip || busyId === notification.id}
                      onCheckedChange={(c) => handleToggle(notification, !!c)}
                      title={
                        isReview
                          ? (canReview ? "Marcar como revisada (libera o envio)" : "Sem permissão")
                          : (isPending
                              ? (canUnreview ? "Desmarcar (volta para revisão)" : "Sem permissão para desmarcar")
                              : "Já enviada — não pode voltar")
                      }
                    />
                  </TableCell>
                  <TableCell className="td font-medium text-blue-900">{notification.notification_category}</TableCell>
                  <TableCell className="td">{notification.cellphone}</TableCell>
                  <TableCell className="td max-w-[500px] truncate">{notification.description}</TableCell>
                  <TableCell className="td">
                    <Badge variant={
                      notification.process_status === 'COMPLETE' ? 'success' :
                      notification.process_status === 'ERROR' ? 'destructive' :
                      notification.process_status === 'REVIEW' ? 'outline' : 'outline'
                    } className="text-[10px]">
                      {notification.process_status}
                    </Badge>
                  </TableCell>
                  <TableCell className="td text-muted-foreground">{notification.created_at}</TableCell>
                  <TableCell className="td text-muted-foreground">
                    {notification.sent_at ?? <span className="text-muted-foreground/50">—</span>}
                  </TableCell>
                  <TableCell className="td">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6"
                      onClick={() => { setSelectedNotification(notification); setDialogOpen(true); }}
                      title="Ver mensagem"
                    >
                      <Eye className="h-3.5 w-3.5 text-muted-foreground" />
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) setSelectedNotification(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader className="border-b pb-2">
            <DialogTitle className="text-sm font-semibold uppercase tracking-wide">Mensagem Enviada</DialogTitle>
            <DialogDescription className="sr-only">Conteúdo da mensagem enviada via WhatsApp</DialogDescription>
          </DialogHeader>
          {(() => {
            const isError = selectedNotification?.process_status === 'ERROR';
            const errorCode = selectedNotification?.provider_error_code;
            const terminalMsg = errorCode ? TWILIO_TERMINAL_ERROR_MESSAGES[errorCode] : null;
            const isTerminal = isError && !!terminalMsg;
            return (
              <>
                <div className="py-2 space-y-3">
                  <p className="text-sm whitespace-pre-wrap leading-relaxed">
                    {selectedNotification?.message_preview || <span className="text-muted-foreground italic">(sem prévia disponível)</span>}
                  </p>
                  {isTerminal && (
                    <div className="rounded-md border border-amber-500/60 bg-amber-50 p-3 flex gap-2">
                      <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
                      <div className="space-y-1">
                        <p className="text-xs font-bold uppercase tracking-wide text-amber-700">Ação necessária</p>
                        <p className="text-xs text-amber-900 leading-relaxed">{terminalMsg}</p>
                        <p className="text-[10px] text-amber-700/70">Código Twilio: {errorCode}</p>
                      </div>
                    </div>
                  )}
                  {isError && !isTerminal && selectedNotification?.error_message && (
                    <div className="rounded-md border border-destructive/50 bg-destructive/5 p-3">
                      <p className="text-xs font-bold uppercase tracking-wide text-destructive mb-1">
                        Erro{errorCode ? ` · código ${errorCode}` : ''}
                      </p>
                      <p className="text-xs whitespace-pre-wrap text-destructive/90 leading-relaxed">
                        {selectedNotification.error_message}
                      </p>
                    </div>
                  )}
                </div>
                {isError && !isTerminal && canResend && (
                  <DialogFooter>
                    <Button
                      variant="default"
                      size="sm"
                      disabled={isResending}
                      onClick={async () => {
                        if (!selectedNotification) return;
                        setIsResending(true);
                        try {
                          await resendNotification(selectedNotification.id);
                          toast({ title: "Notificação reenfileirada — será enviada na próxima rodada." });
                          setDialogOpen(false);
                          onChanged?.();
                        } catch (error) {
                          toast({
                            title: "Erro ao reenviar",
                            description: error instanceof Error ? error.message : "Tente novamente.",
                            variant: "destructive",
                          });
                        } finally {
                          setIsResending(false);
                        }
                      }}
                      className="gap-1.5"
                    >
                      <RefreshCw className={`h-4 w-4 ${isResending ? "animate-spin" : ""}`} />
                      {isResending ? "Reenviando..." : "Reenviar"}
                    </Button>
                  </DialogFooter>
                )}
                {isError && isTerminal && canResend && (
                  <DialogFooter>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={isResettingError}
                      onClick={async () => {
                        if (!selectedNotification) return;
                        setIsResettingError(true);
                        try {
                          await resetErrorNotification(selectedNotification.id);
                          toast({ title: "Erro resetado — notificação será reenviada (confirme se o cadastro foi corrigido)." });
                          setDialogOpen(false);
                          onChanged?.();
                        } catch (error) {
                          toast({
                            title: "Erro ao resetar",
                            description: error instanceof Error ? error.message : "Tente novamente.",
                            variant: "destructive",
                          });
                        } finally {
                          setIsResettingError(false);
                        }
                      }}
                      className="gap-1.5"
                    >
                      <RefreshCw className={`h-4 w-4 ${isResettingError ? "animate-spin" : ""}`} />
                      {isResettingError ? "Resetando..." : "Resetar erro e reenviar"}
                    </Button>
                  </DialogFooter>
                )}
              </>
            );
          })()}
        </DialogContent>
      </Dialog>
    </>
  );
}
